import { REQUISITION_DEPARTMENTS, REQUISITION_PRIORITIES } from '@felix/domain';
import {
  ActionIcon,
  Button,
  NumberInput,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconShip, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { PageHeader } from '../../PageHeader.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useUnsavedChanges } from '../../shell-context.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { MaterialPicker } from './MaterialPicker.js';
import { pricesVisible } from './orders.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './Purchasing.module.css';
import {
  emptyForm,
  formatAmount,
  formatQuantity,
  formFromDetail,
  formIsDirty,
  formTotal,
  lineKey,
  lineTotal,
  linkedForm,
  numberSeparators,
  pickerClassOf,
  type RequisitionFormValues,
  type RequisitionLink,
  shortfallForm,
  toInput,
  validateForm,
} from './requisitions.js';
import type { PurchasingSource, RequisitionEditor } from './source.js';
import { LoadError } from './table.js';
import type { RequisitionDetail, RequisitionFormOptions, RequisitionPriority } from './types.js';

type Loaded = {
  options: RequisitionFormOptions;
  vessel: PersonnelVessel | null;
  detail: RequisitionDetail | null;
};

/**
 * Yeni ya da düzeltilen talep (`satin-alma-akisi.jpeg` "New Material Requisition"): 1. Header
 * Information (Vessel, Department, Requested By, Required Date, Port, Remarks), 2. Add Items
 * (katalogdan ya da serbest metin; miktar, birim fiyat, öncelik). "Save & Submit" parolayla
 * imzalayıp onaya gönderir; "Save Draft" yalnız kaydeder. Yalnız gemide (ADR 0026 §1).
 */
export function RequisitionForm({
  source,
  editor,
  requisitionId,
  shortfallOf = null,
  link = null,
  department = null,
  onDone,
  onCancel,
}: {
  source: PurchasingSource;
  editor: RequisitionEditor;
  /** Düzeltilen talep; verilmezse yeni. */
  requisitionId: string | null;
  /** Eksik için yeni talep: kalan miktarlar bu talepten gelir (ADR 0047 §6). */
  shortfallOf?: string | null;
  /** Arızadan ya da HSEQ raporundan yeni talep (ADR 0096 §4). */
  link?: RequisitionLink | null;
  /** Önceden seçili departman (kumanya talebi: Provisions; ADR 0099). */
  department?: string | null;
  onDone: (id: string, message: string) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.form.${key}`, options);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [initial, setInitial] = useState<RequisitionFormValues | null>(null);
  const [values, setValues] = useState<RequisitionFormValues | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState<{ id: string; message: string } | null>(null);
  const finished = useRef(false);

  // Bağ yoldan gelir; nesne her çizimde yenidir, alanlarına bağlıdır (ADR 0096 §4)
  // biome-ignore lint/correctness/useExhaustiveDependencies: link nesnesi değil alanları
  const load = useCallback(() => {
    let alive = true;
    setLoadError(null);
    Promise.all([
      editor.form(),
      source.vessels(),
      requisitionId ? source.requisition(requisitionId) : Promise.resolve(null),
      shortfallOf ? source.requisition(shortfallOf) : Promise.resolve(null),
    ])
      .then(([options, vessels, detail, shortfall]) => {
        if (!alive) return;
        const empty = emptyForm(options);
        let form = detail ? formFromDetail(detail) : empty;
        if (!detail && shortfall) {
          const prefilled = shortfallForm(shortfall, shortfall.requisitionNo, {
            orderPrices: pricesVisible(source.scope),
          });
          // Talep eden gemide değilse formdaki varsayılan (oturumdaki kişi)
          form = options.requesters.some((r) => r.id === prefilled.requestedBy)
            ? prefilled
            : { ...prefilled, requestedBy: empty.requestedBy };
        }
        if (!detail && !shortfall && link) form = linkedForm(form, link);
        if (!detail && !shortfall && department) form = { ...form, department };
        setLoaded({ options, vessel: vessels[0] ?? null, detail });
        // Önceden doldurulmuş eksik talebi kaydedilmemiş değişikliktir
        setInitial(detail ? form : empty);
        setValues(form);
      })
      .catch((error) => {
        if (alive) setLoadError(errorCode(error));
      });
    return () => {
      alive = false;
    };
  }, [
    editor,
    source,
    requisitionId,
    shortfallOf,
    link?.defectId,
    link?.hseReportId,
    link?.ref,
    department,
  ]);
  useEffect(() => {
    void attempt;
    return load();
  }, [load, attempt]);

  // Kaydedilmemiş değişiklik: menü, yol ya da arama gezinmesi onay ister (M6f §0.3)
  const dirty = initial !== null && values !== null && formIsDirty(initial, values);
  useUnsavedChanges(dirty && !busy && done === null);
  // Kayıttan sonraki gezinme uyarı kaydı temizlendikten sonra (aynı commit'te önceki efekt)
  useEffect(() => {
    if (!done || finished.current) return;
    finished.current = true;
    onDone(done.id, done.message);
  }, [done, onDone]);

  if (loadError) {
    return (
      <div className={classes.screen}>
        <PageHeader title={k('newTitle')} crumb={k('newCrumb')} />
        <LoadError title={k('error')} code={loadError} onRetry={() => setAttempt((n) => n + 1)} />
      </div>
    );
  }
  if (!loaded || !values) {
    return (
      <div className={classes.screen} aria-busy>
        <PageHeader title={k('newTitle')} crumb={k('newCrumb')} />
        <section className={classes.section} aria-label={k('loading')}>
          <Skeleton height={18} width={220} />
          <div className={classes.headerGrid}>
            {['vessel', 'department', 'requestedBy', 'requiredDate', 'port', 'reason'].map(
              (field) => (
                <Skeleton key={field} height={58} />
              ),
            )}
          </div>
        </section>
        <section className={classes.section}>
          <Skeleton height={18} width={160} />
          <Skeleton height={36} width={150} />
          <Skeleton height={120} />
        </section>
      </div>
    );
  }

  const { options, vessel, detail } = loaded;
  const currency = values.currency;
  const set = (patch: Partial<RequisitionFormValues>) => setValues({ ...values, ...patch });
  const setLine = (index: number, patch: Partial<RequisitionFormValues['lines'][number]>) =>
    set({ lines: values.lines.map((line, i) => (i === index ? { ...line, ...patch } : line)) });
  const fieldError = (path: string) =>
    errors[path]
      ? t(`purchasing.field.${errors[path]}`, { defaultValue: errors[path] })
      : undefined;
  const total = formTotal(values.lines);
  const today = options.today;
  const requesterLabel = (id: string) => {
    const person = options.requesters.find((r) => r.id === id);
    if (!person) return id;
    const sameRole = options.requesters.filter((r) => r.role === person.role).length > 1;
    return sameRole || !person.role ? `${person.role ?? ''} · ${person.name}` : person.role;
  };

  /**
   * Kaydeder; gönderimde kayıttan sonra imzalar (mimari belge §7). Parola yalnız girildiyse
   * gönderilir (ADR 0075).
   */
  async function save(submit: { password?: string } | null): Promise<string | null> {
    if (!values) return null;
    const found = validateForm(values, { submit: submit !== null, today });
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setSaveError('fieldsInvalid');
      return 'VALIDATION_FAILED';
    }
    setBusy(true);
    setSaveError(null);
    try {
      const input = toInput(values);
      let saved = detail
        ? await editor.update(detail.id, loaded?.detail?.rowVersion ?? detail.rowVersion, input)
        : await editor.create(input);
      // Kaydedildi: yeniden kaydetmede düzeltme olur (çift talep açılmaz)
      setLoaded((current) => (current ? { ...current, detail: saved } : current));
      if (submit) {
        saved = await editor.submit(saved.id, {
          contentHash: saved.contentHash,
          ...(submit.password ? { password: submit.password } : {}),
        });
        setDone({ id: saved.id, message: k('submitted', { no: saved.requisitionNo }) });
      } else {
        setDone({ id: saved.id, message: k('saved', { no: saved.requisitionNo }) });
      }
      return null;
    } catch (error) {
      const code = errorCode(error);
      if (!submit) setSaveError(code);
      return code;
    } finally {
      setBusy(false);
    }
  }

  const title = detail ? k('editTitle', { no: detail.requisitionNo }) : k('newTitle');

  return (
    <div className={classes.screen}>
      <PageHeader
        title={title}
        description={k('description')}
        crumb={detail ? detail.requisitionNo : k('newCrumb')}
      />

      <form
        className={classes.screen}
        aria-label={title}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          const found = validateForm(values, { submit: true, today });
          setErrors(found);
          if (Object.keys(found).length > 0) {
            setSaveError('fieldsInvalid');
            return;
          }
          setSaveError(null);
          setSigning(true);
        }}
      >
        {values.defectId || values.hseReportId ? (
          <p className={classes.notice} data-tone="info" role="note">
            {k('linked', { no: values.defectReportNo || '—' })}
          </p>
        ) : null}
        <section className={classes.section} aria-labelledby="requisition-header">
          <h2 id="requisition-header" className={classes.sectionTitle}>
            <span className={classes.sectionNumber}>1.</span> {k('header')}
          </h2>
          <div className={classes.headerGrid}>
            <div className={classes.field}>
              <Select
                label={k('vessel')}
                required
                leftSection={<IconShip size={16} aria-hidden />}
                data={
                  vessel
                    ? [{ value: vessel.id, label: `${vessel.name} (${vessel.imo ?? ''})` }]
                    : []
                }
                value={vessel?.id ?? null}
                readOnly
                // Gemide salt okunur seçici: ok yok (M6f §0.6)
                rightSection={null}
              />
            </div>
            <div className={classes.field}>
              <Select
                label={k('department')}
                required
                placeholder={k('choose')}
                data={REQUISITION_DEPARTMENTS.map((value) => ({
                  value,
                  label: t(`purchasing.departments.${value}`),
                }))}
                value={values.department || null}
                onChange={(value) => set({ department: value ?? '' })}
                error={fieldError('department')}
              />
            </div>
            <div className={classes.field}>
              <Select
                label={k('requestedBy')}
                required
                placeholder={k('choose')}
                data={options.requesters.map((person) => ({
                  value: person.id,
                  label: requesterLabel(person.id),
                }))}
                value={values.requestedBy || null}
                onChange={(value) => set({ requestedBy: value ?? '' })}
                error={fieldError('requestedBy')}
              />
            </div>
            <div className={classes.field}>
              <DayInput
                label={k('requiredDate')}
                required
                minDate={today}
                value={values.requiredDate}
                onChange={(value) => set({ requiredDate: value ?? '' })}
                error={fieldError('requiredDate')}
              />
            </div>
            <div className={classes.field}>
              {/* Liman listesinden; listede yoksa metin (ADR 0048 §6) */}
              <PortPicker
                label={k('port')}
                placeholder={k('portPlaceholder')}
                value={{ name: values.port, code: values.portCode }}
                onChange={(port) => set({ port: port.name.slice(0, 120), portCode: port.code })}
                error={fieldError('port') ?? fieldError('portCode')}
              />
            </div>
            <div className={`${classes.field} ${classes.remarks}`}>
              <Textarea
                label={k('remarks')}
                maxLength={1000}
                autosize
                minRows={1}
                maxRows={4}
                value={values.remarks}
                onChange={(event) => set({ remarks: event.currentTarget.value })}
                error={fieldError('remarks')}
              />
            </div>
          </div>
        </section>

        <section className={classes.section} aria-labelledby="requisition-items">
          <div className={classes.itemsHead}>
            <h2 id="requisition-items" className={classes.sectionTitle}>
              <span className={classes.sectionNumber}>2.</span> {k('items')}
            </h2>
          </div>
          <div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setPicking(true)}>
              {k('addMaterial')}
            </Button>
          </div>
          {errors.lines ? (
            <p className={classes.error} role="alert">
              {t(`purchasing.field.${errors.lines}`)}
            </p>
          ) : null}
          {values.lines.length === 0 ? (
            <EmptyState title={k('noItems')} />
          ) : (
            <section
              className={table.tableWrap}
              aria-label={k('items')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${classes.table} ${classes.lineTable}`}>
                <thead>
                  <tr>
                    <th scope="col" className={classes.index}>
                      {k('columns.no')}
                    </th>
                    <th scope="col" className={classes.key}>
                      {k('columns.material')}
                    </th>
                    <th scope="col">{k('columns.description')}</th>
                    <th scope="col">{k('columns.qty')}</th>
                    <th scope="col">{k('columns.uom')}</th>
                    <th scope="col">{k('columns.unitPrice', { currency })}</th>
                    <th scope="col" data-numeric>
                      {k('columns.total', { currency })}
                    </th>
                    <th scope="col">{k('columns.priority')}</th>
                    <th scope="col">
                      <span className={classes.visuallyHidden}>{k('columns.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {values.lines.map((line, index) => {
                    const amount = lineTotal(line);
                    const label = (column: string) =>
                      `${line.name} — ${k(`columns.${column}`, { currency })}`;
                    return (
                      <tr key={line.key}>
                        <td className={classes.index}>{index + 1}</td>
                        <td className={`${classes.material} ${classes.key}`}>
                          {line.name}
                          {/* Stok mevcudu (ADR 0111 §A; sahip: "örneğin 5 adet mevcut desin") */}
                          {line.onHand !== undefined && line.onHand !== null && line.onHand > 0 ? (
                            <span className={classes.stockLine}>
                              <StatusBadge tone="info">
                                {t('purchasing.review.inStock', {
                                  qty: formatQuantity(line.onHand, locale),
                                })}
                              </StatusBadge>
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <TextInput
                            className={classes.lineDescription}
                            aria-label={label('description')}
                            maxLength={300}
                            value={line.description}
                            onChange={(event) =>
                              setLine(index, { description: event.currentTarget.value })
                            }
                          />
                        </td>
                        <td>
                          <NumberInput
                            className={classes.qty}
                            aria-label={label('qty')}
                            min={0}
                            decimalScale={3}
                            hideControls
                            {...numberSeparators(locale)}
                            value={line.quantity}
                            onChange={(value) => setLine(index, { quantity: value })}
                            error={errors[`lines.${index}.quantity`] ? true : undefined}
                          />
                        </td>
                        <td>{line.unit}</td>
                        <td>
                          <NumberInput
                            className={classes.price}
                            aria-label={label('unitPrice')}
                            min={0}
                            decimalScale={2}
                            hideControls
                            {...numberSeparators(locale)}
                            value={line.unitPrice}
                            onChange={(value) => setLine(index, { unitPrice: value })}
                            error={errors[`lines.${index}.unitPrice`] ? true : undefined}
                          />
                        </td>
                        <td data-numeric>{formatAmount(amount, locale)}</td>
                        <td>
                          <Select
                            className={classes.priority}
                            aria-label={label('priority')}
                            data={REQUISITION_PRIORITIES.map((value) => ({
                              value,
                              label: t(`purchasing.priorities.${value}`),
                            }))}
                            value={line.priority}
                            allowDeselect={false}
                            onChange={(value) =>
                              setLine(index, {
                                priority: (value ?? 'Normal') as RequisitionPriority,
                              })
                            }
                          />
                        </td>
                        <td>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={k('remove', { name: line.name })}
                            onClick={() =>
                              set({ lines: values.lines.filter((_, i) => i !== index) })
                            }
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={classes.totalRow}>
                    <td colSpan={6} data-numeric>
                      {k('total')}
                    </td>
                    <td data-numeric title={total.value === null ? k('unknownTotal') : undefined}>
                      {formatAmount(total.priced, locale)}
                      {total.value === null ? '*' : ''}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tbody>
              </table>
            </section>
          )}
        </section>

        {saveError ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {saveError === 'fieldsInvalid'
              ? k('fieldsInvalid')
              : t(`purchasing.errors.${saveError}`, {
                  defaultValue: t('purchasing.errors.GENERIC'),
                })}
          </p>
        ) : null}

        <div className={classes.footer}>
          <Button variant="default" onClick={onCancel} disabled={busy}>
            {k('cancel')}
          </Button>
          <Button variant="default" loading={busy} onClick={() => void save(null)}>
            {k('saveDraft')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('saveSubmit')}
          </Button>
        </div>
      </form>

      <MaterialPicker
        opened={picking}
        source={source}
        existing={values.lines.length}
        materialClass={pickerClassOf(values.department)}
        onClose={() => setPicking(false)}
        onAdd={(items) =>
          set({
            lines: [
              ...values.lines,
              ...items.map((item) => ({
                key: lineKey(),
                materialId: item.materialId,
                name: item.name,
                description: '',
                quantity: item.quantity,
                unit: item.unit,
                unitPrice: '' as const,
                priority: 'Normal' as const,
                ...(item.onHand === undefined ? {} : { onHand: item.onHand }),
              })),
            ],
          })
        }
      />
      <PasswordDialog
        opened={signing}
        title={t('purchasing.password.submitTitle')}
        body={t('purchasing.password.submitBody')}
        reason="none"
        password
        reauthKind="requisition"
        confirmLabel={k('saveSubmit')}
        onClose={() => setSigning(false)}
        onConfirm={async ({ password }) => {
          const code = await save(password ? { password } : {});
          if (!code) setSigning(false);
          return code;
        }}
      />
    </div>
  );
}
